import { Routes, Route } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { useIsAuthenticated } from './hooks';
import {
  AccountDetails,
  AccountStatement,
  AccountSummary,
  ActivityLog,
  ActivityLogs,
  AffiliateListing,
  AffiliateReq,
  AllDownlineListing,
  BankerDashboard,
  BankerDetail,
  Bankers,
  BannerManagement,
  BetList,
  BetSetting,
  BussinessReports,
  CasinoBetList,
  CasinoControl,
  CasinoDetail,
  CasinoPlByDownline,
  CasinoPlByPlayer,
  CategoryControl,
  ComissionReport,
  AffiliateActiveUserOfWeek,
  ContentManagement,
  DashBoard,
  DashboardPage,
  DWRequest,
  EventManagement,
  Export,
  FilteredGames,
  GameControl,
  InactiveMemerList,
  Login,
  ManageAccount,
  NotFoundPage,
  NotificationManagement,
  OnboardingPage,
  PlByEvent,
  PlByPlayer,
  PlDownline,
  ProviderControl,
  Risk,
  Setting,
  SubUsers,
  SubUsersListing,
  SupportContactManagement,
  TransactionCode,
  TransactionHistory,
  UserAccountStatement,
  UserBetHistory,
  UserBettingPL,
  RoleWiseAccountStatement,
  CryptoConversion,
  BonusStatement,
  PolicyManagement,
  RiskDetailsPage,
  Settlement,
} from '@/components/routes';
import AffiliateSetting from './pages/AffiliateManagement/AffiliateSetting';
import { useRoleCheck } from './hooks/useRoleCheck';
import ProtectedAdminRoutes from './components/routes/ProtectedAdminRoutes';
import MaintenanceRoute from './components/routes/MaintenanceRoute';

export function App() {
  const isAuth = useIsAuthenticated();

  const user = useSelector((state: RootState) => state.user);
  const userRole = user?.data?.role?.name;

  const { isOwner } = useRoleCheck();
  const ownerOnly = (element: React.ReactNode) => (
    <ProtectedAdminRoutes allowed={isOwner}>{element}</ProtectedAdminRoutes>
  );
  return (
    <Routes>
      <Route element={<MaintenanceRoute />}>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/register" element={<RegisterPage />} /> */}
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            {isAuth && userRole !== 'BANKER' ? (
              <>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
              </>
            ) : (
              <>
                <Route path="/" element={<BankerDashboard />} />
                <Route path="/dashboard" element={<BankerDashboard />} />
              </>
            )}
            {/* <Route path="/profile" element={<ProfilePage />} /> */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* Downline Routes */}
            <Route path="/members/:id" element={<AllDownlineListing />} />
            <Route path="/members/:id/:role/:user/:memberid/:upline" element={<SubUsers />} />
            <Route path="/settlement" element={<Settlement />} />
            <Route path="/members/:id/sub/:memberid/:userType" element={<AccountDetails />}>
              <Route index element={<AccountSummary />} />
              <Route path="account-summary" element={<AccountSummary />} />

              <Route path="account-statement" element={<UserAccountStatement />} />
              <Route path="bet-history" element={<UserBetHistory />} />
              <Route path="betting-pl" element={<UserBettingPL />} />

              <Route path="casino-detail" element={<CasinoDetail />} />

              <Route path="transaction-history" element={<TransactionHistory />} />
              <Route path="login-history" element={<ActivityLog />} />
            </Route>

            {/* Affiliate Management */}
            <Route path="/affiliate-dashboard" element={<DashBoard />} />
            <Route path="/affiliate-req" element={<AffiliateReq />} />
            <Route path="/affiliate-list" element={<AffiliateListing />} />
            <Route path="/affiliate-list/:id" element={<SubUsersListing />} />
            <Route path="/affiliate-comission-report" element={<ComissionReport />} />
            <Route path="/affiliate-comission-report/:id" element={<AffiliateActiveUserOfWeek />} />
            <Route path="/affiliate-setting" element={<AffiliateSetting />} />

            {/* Change Password Routes */}
            <Route path="/settings" element={<Setting />} />

            {/* Event Management*/}
            <Route path="/event-management/:status?" element={ownerOnly(<EventManagement />)} />
            <Route path="/bet-setting" element={ownerOnly(<BetSetting />)} />
            {/* Casino management */}
            <Route path="/casino-management" element={ownerOnly(<CasinoControl />)}>
              <Route index element={<CategoryControl />} />
              <Route path="provider" element={<ProviderControl />} />
              <Route path="game" element={<GameControl />} />
            </Route>
            <Route path="/casino-management/games" element={ownerOnly(<FilteredGames />)} />
            {/* Content Management */}
            <Route path="/content-management" element={ownerOnly(<ContentManagement />)}>
              <Route index element={<BannerManagement />} />
              <Route path="notification-management" element={<NotificationManagement />} />
              <Route path="support-management" element={<SupportContactManagement />} />
            </Route>
            {/* Bonus Management */}
            <Route path="/bonus">
              <Route path="policy-management" element={ownerOnly(<PolicyManagement />)} />
              <Route path="statement" element={ownerOnly(<BonusStatement />)} />
            </Route>
            {/* Banker Management */}
            <Route path="/bankers" element={ownerOnly(<Bankers />)} />
            <Route path="/bankers/:id/:details?/:mode?/:type?" element={<BankerDetail />} />
            <Route path="/requests/:mode?/:type?" element={ownerOnly(<DWRequest />)} />
            <Route path="/manage-account/:type?" element={ownerOnly(<ManageAccount />)} />
            <Route path="/crypto-conversion" element={ownerOnly(<CryptoConversion />)} />
            <Route path="/account-statement" element={<AccountStatement />} />
            <Route path="/banker-dashboard" element={<BankerDashboard />} />
            <Route path="/rolewise-account-statement" element={<RoleWiseAccountStatement />} />

            {/* Risk Management Routes */}
            <Route path="/risk" element={<Risk />} />
            <Route path="/risk-details" element={<RiskDetailsPage />} />

            {/* Reports Routes */}
            <Route path="/bet-list" element={<BetList />} />
            <Route path="/casino-bet-list" element={<CasinoBetList />} />
            <Route path="/pl-by-downline" element={<PlDownline />} />
            <Route path="/pl-by-player" element={<PlByPlayer />} />
            <Route path="/pl-by-event" element={<PlByEvent />} />
            <Route path="/casinopl-by-player" element={<CasinoPlByPlayer />} />
            <Route path="/casinopl-by-downline" element={<CasinoPlByDownline />} />
            <Route path="/inactive-members" element={<InactiveMemerList />} />
            {/* activity logs */}
            <Route path="/activity-logs" element={<ActivityLogs />} />

            <Route path="/business-reports" element={<BussinessReports />} />

            {/* Export History */}
            <Route path="/export" element={<Export />} />
          </Route>
        </Route>
        {/* </Route> */}
        <Route path="/transaction-code" element={<TransactionCode />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
